import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import api from '../utils/api';
import { Calendar, Clock, BookOpen, ChevronRight, PlusSquare, MapPin, Building2 } from 'lucide-react';
import { formatDateBadge, formatTanggalID, todayStr } from '../utils/dateUtils';
import { SkeletonCard } from '../components/Skeleton';

const formatJam = (j) => j ? j.slice(0, 5) : '--:--';

export default function DashboardPage() {
  const { user } = useAuth();
  const { isAdmin } = useAuth();

  const navigate = useNavigate();
  const [stats, setStats] = useState({ total: 0, bulanIni: 0, hariIni: 0, recent: [] });
  const [loading, setLoading] = useState(true);

  const today = new Date().toLocaleDateString('id-ID', {
    weekday: 'long', day: 'numeric', month: 'long', year: 'numeric'
  });

  useEffect(() => {
    document.title = "Dashboard - Penjadwalan Acara Pimpinan";
    api.get('/jadwal/dashboard')
      .then(res => setStats(res.data.data))
      .catch(console.error)
      .finally(() => setLoading(false));
      
  }, []);

  return (
    <div className="page-container">
      {/* Hero */}
      <div className="dashboard-hero">
        <div style={{ position: 'relative', zIndex: 1 }}>
          <div style={{ display: 'inline-flex', alignItems: 'center', gap: 8, background: 'rgba(255,255,255,0.15)', borderRadius: 100, padding: '6px 14px', marginBottom: 20, fontSize: 13, fontWeight: 500, backdropFilter: 'blur(10px)' }}>
            <Building2 size={14} /> Sekretariat DPRD Provinsi DKI Jakarta
          </div>
          <p style={{ fontSize: 16, opacity: 0.8, marginBottom: 4 }}>Penjadwalan Acara Pimpinan</p>
          <p style={{ fontSize: 13, opacity: 0.6 }}>Sistem manajemen jadwal kegiatan pimpinan yang terintegrasi</p>
        </div>
      </div>

      {/* Stats */}
      <div className="grid-3" style={{ marginBottom: 28 }}>
        {loading ? (
          <>
            <SkeletonCard /><SkeletonCard /><SkeletonCard />
          </>
        ) : (
          <>
            <div className="stat-card">
              <div>
                <div className="stat-label">Total Agenda</div>
                <div className="stat-value">{stats.total}</div>
                <div className="stat-sub">Seluruh jadwal tersimpan</div>
              </div>
              <div className="stat-icon" style={{ background: 'var(--purple-bg)' }}>
                <BookOpen size={24} color="var(--purple)" />
              </div>
            </div>
            <div className="stat-card">
              <div>
                <div className="stat-label">Bulan Ini</div>
                <div className="stat-value">{stats.bulanIni}</div>
                <div className="stat-sub">{new Date().toLocaleString('id-ID', { month: 'long', year: 'numeric' })}</div>
              </div>
              <div className="stat-icon" style={{ background: 'var(--secondary-bg)' }}>
                <Calendar size={24} color="var(--secondary)" />
              </div>
            </div>
            <div className="stat-card" onClick={() => navigate('/jadwal')} style={{ cursor: 'pointer' }}>
              <div>
                <div className="stat-label">Hari Ini</div>
                <div className="stat-value">{stats.hariIni}</div>
                <div className="stat-sub" style={{ color: 'var(--primary)', fontWeight: 600 }}>
                  Klik untuk lihat detail
                </div>
              </div>
              <div className="stat-icon" style={{ background: 'var(--orange-bg)' }}>
                <Clock size={24} color="var(--orange)" />
              </div>
            </div>
          </>
        )}
      </div>


      {isAdmin && (
        <div className="grid-2" style={{ marginBottom: 28 }}>
          <div className="card" style={{ cursor: 'pointer', overflow: 'hidden', position: 'relative' }} onClick={() => navigate('/jadwal')}>
            <div style={{ position: 'absolute', top: -20, right: -20, width: 120, height: 120, background: 'var(--primary-bg)', borderRadius: '50%', opacity: 0.5 }} />
            <div className="card-body">
              <div style={{ width: 52, height: 52, background: 'var(--primary-bg)', borderRadius: 14, display: 'flex', alignItems: 'center', justifyContent: 'center', marginBottom: 16 }}>
                <Calendar size={26} color="var(--primary)" />
              </div>
              <h3 style={{ fontSize: 18, fontWeight: 700, marginBottom: 6 }}>Lihat Jadwal</h3>
              <p style={{ fontSize: 13, color: 'var(--gray-500)', marginBottom: 16, lineHeight: 1.6 }}>
                Tampilan kalender interaktif untuk melihat seluruh jadwal kegiatan pimpinan
              </p>
              <span style={{ color: 'var(--primary)', fontSize: 13, fontWeight: 600, display: 'flex', alignItems: 'center', gap: 4 }}>
                Buka Kalender <ChevronRight size={16} />
              </span>
            </div>
          </div>

          <div className="card" style={{ cursor: 'pointer', overflow: 'hidden', position: 'relative' }} onClick={() => navigate('/input')}>
            <div style={{ position: 'absolute', top: -20, right: -20, width: 120, height: 120, background: 'var(--secondary-bg)', borderRadius: '50%', opacity: 0.5 }} />
            <div className="card-body">
              <div style={{ width: 52, height: 52, background: 'var(--secondary-bg)', borderRadius: 14, display: 'flex', alignItems: 'center', justifyContent: 'center', marginBottom: 16 }}>
                <PlusSquare size={26} color="var(--secondary)" />
              </div>
              <h3 style={{ fontSize: 18, fontWeight: 700, marginBottom: 6 }}>Input Jadwal</h3>
              <p style={{ fontSize: 13, color: 'var(--gray-500)', marginBottom: 16, lineHeight: 1.6 }}>
                Tambahkan agenda baru ke dalam sistem penjadwalan kegiatan pimpinan
              </p>
              <span style={{ color: 'var(--secondary)', fontSize: 13, fontWeight: 600, display: 'flex', alignItems: 'center', gap: 4 }}>
                Buat Jadwal Baru <ChevronRight size={16} />
              </span>
            </div>
          </div>
        </div>
      )}

      {/* Recent jadwal */}
      <div className="card">
        <div className="card-header">
          <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
            <div style={{ width: 36, height: 36, background: 'var(--orange-bg)', borderRadius: 10, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
              <Clock size={18} color="var(--orange)" />
            </div>
            <div>
              <div style={{ fontSize: 16, fontWeight: 700 }}>Jadwal Terbaru</div>
              <div style={{ fontSize: 12, color: 'var(--gray-400)' }}>Agenda yang baru diinput ke sistem</div>
            </div>
          </div>
          <button className="btn btn-ghost btn-sm" onClick={() => navigate('/rekap')}>
            Lihat Semua <ChevronRight size={14} />
          </button>
        </div>

        <div style={{ padding: '8px 0' }}>
          {loading ? (
            <div style={{ padding: 40, textAlign: 'center', color: 'var(--gray-400)' }}>Memuat data...</div>
          ) : stats.recent.length === 0 ? (
            <div className="empty-state">
              <div className="empty-state-icon">📅</div>
              <p>Belum ada jadwal tersimpan</p>
            </div>
          ) : (
            stats.recent.map((j, i) => {
              // Gunakan formatDateBadge — fix UTC bug
              const { day, month } = formatDateBadge(j.tanggal);
              return (
                <div key={j.id}
                  style={{ display: 'flex', alignItems: 'center', gap: 16, padding: '16px 24px', borderBottom: i < stats.recent.length - 1 ? '1px solid var(--gray-100)' : 'none', cursor: 'pointer', transition: 'background 0.15s' }}
                  onClick={() => navigate('/jadwal')}
                  onMouseEnter={e => e.currentTarget.style.background = 'var(--gray-50)'}
                  onMouseLeave={e => e.currentTarget.style.background = 'transparent'}
                >
                  <div className="date-badge">
                    <div className="date-badge-day">{day}</div>
                    <div className="date-badge-month">{month}</div>
                  </div>
                  <div style={{ flex: 1 }}>
                    <div style={{ fontWeight: 600, fontSize: 14 }}>{j.nama_acara}</div>
                    <div style={{ fontSize: 12, color: 'var(--gray-400)', marginTop: 2, display: 'flex', alignItems: 'center', gap: 8 }}>
                      <span>{formatJam(j.jam_mulai)} – {formatJam(j.jam_selesai)}</span>
                      {j.lokasi && <><span>•</span><span style={{ display: 'flex', alignItems: 'center', gap: 3 }}><MapPin size={11} />{j.lokasi}</span></>}
                    </div>
                  </div>
                  <div style={{ fontSize: 12, color: 'var(--gray-400)', textAlign: 'right' }}>
                    {j.penyelenggara || '—'}
                  </div>
                </div>
              );
            })
          )}
        </div>
      </div>
    </div>
  );
}
